import { TypedDocumentNode, gql } from '@apollo/client';
import {
  FormattedDateTimeRange,
  FormattedMessage,
  FormattedNumber,
} from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Neutral } from '@sorare/core/src/atoms/icons/Neutral';
import { Promotion } from '@sorare/core/src/atoms/icons/Promotion';
import { Relegation } from '@sorare/core/src/atoms/icons/Relegation';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { RivalsLightning } from '@sorare/core/src/components/rivals/RepScore/RivalsLightning';
import { glossary, rivals } from '@sorare/core/src/lib/glossary';

import { SeasonsHistory_footballRivalsSeason } from './__generated__/index.graphql';

const Root = styled(Box)`
  padding: var(--double-unit);
`;
const Table = styled.table`
  border-spacing: 0;
  width: 100%;
`;
const TableHeaderRow = styled.tr`
  color: var(--c-neutral-600);

  & > th {
    text-align: left;
    vertical-align: middle;
    padding-left: var(--intermediate-unit);
    padding-bottom: var(--intermediate-unit);
    align-items: center;
  }
`;
const Row = styled.tr`
  & > td {
    padding-left: var(--intermediate-unit);
  }
  & > td:last-child {
    width: 1%;
    white-space: nowrap;
  }
`;
const TableCellWithBackground = styled.td`
  &.promoted {
    background: linear-gradient(
      to right,
      rgba(var(--c-rgb-rivals-loss), 0.2),
      transparent 100%
    );
  }
  &.relegated {
    background: linear-gradient(
      to right,
      rgba(var(--c-rgb-rivals-loss), 0.2),
      transparent 100%
    );
  }
`;

const getIconAndColor = (divisionChange?: DivisionChange | null) => {
  switch (divisionChange) {
    case DivisionChange.PROMOTED:
      return {
        color: 'var(--c-rivals-friendlies)',
        backgroundColor: 'rgba(var(--c-rgb-rivals-friendlies), 0.2)',
        icon: <Promotion fill="var(--c-rivals-friendlies)" />,
      } as const;
    case DivisionChange.RELEGATED:
      return {
        color: 'var(--c-rivals-loss)',
        backgroundColor: 'rgba(var(--c-rgb-rivals-loss), 0.2)',
        icon: <Relegation />,
      } as const;
    default:
      return {
        color: 'var(--c-neutral-1000)',
        backgroundColor: null,
        icon: <Neutral />,
      } as const;
  }
};

type Props = {
  pastSeasons?: SeasonsHistory_footballRivalsSeason[];
  loading: boolean;
};
export const SeasonsHistory = ({ pastSeasons, loading }: Props) => {
  if (loading && !pastSeasons) {
    return (
      <Skeleton loading>
        <Root style={{ height: '173px' }} />
      </Skeleton>
    );
  }

  if (!pastSeasons?.some(season => season.ranking)) {
    return null;
  }

  return (
    <Root>
      <Table>
        <thead>
          <TableHeaderRow>
            <th>
              <LabelS as="span">
                <FormattedMessage {...glossary.week} />
              </LabelS>
            </th>
            <th>
              <Tooltip
                title={
                  <LabelS>
                    <FormattedMessage {...rivals.leaderboardPosition} />
                  </LabelS>
                }
              >
                <LabelS as="span">
                  <FormattedMessage
                    {...rivals.leaderboardPositionAbbreviated}
                  />
                </LabelS>
              </Tooltip>
            </th>
            <th>
              <LabelS as="span">
                <FormattedMessage {...rivals.rep} />
              </LabelS>
            </th>
            <th>
              <LabelS as="span">
                <FormattedMessage {...rivals.division} />
              </LabelS>
            </th>
          </TableHeaderRow>
        </thead>
        <tbody>
          {pastSeasons.map(
            ({ id, startDate, endDate, ranking: rankingObject }) => {
              if (!rankingObject) {
                return null;
              }
              const { divisionChange, points, ranking, divisionConfig } =
                rankingObject || {};
              const { color, backgroundColor, icon } =
                getIconAndColor(divisionChange);
              return (
                <Row key={id}>
                  <td>
                    <LabelL>
                      <FormattedDateTimeRange
                        from={startDate}
                        to={endDate}
                        day="numeric"
                        month="short"
                      />
                    </LabelL>
                  </td>
                  <td>
                    <LabelL bold color={color}>
                      <FormattedNumber value={ranking} />
                    </LabelL>
                  </td>
                  <td>
                    <Horizontal gap={0.5}>
                      <LabelL bold>
                        <FormattedNumber value={points} />
                      </LabelL>
                      <RivalsLightning fill="var(--c-rivals-reputation)" />
                    </Horizontal>
                  </td>
                  <TableCellWithBackground
                    style={{
                      background: `linear-gradient(to right, ${backgroundColor}, transparent 100%)`,
                    }}
                  >
                    <Horizontal gap={0.5}>
                      {icon}
                      <LabelL bold uppercase>
                        {divisionConfig.shortTitle}
                      </LabelL>
                    </Horizontal>
                  </TableCellWithBackground>
                </Row>
              );
            }
          )}
        </tbody>
      </Table>
    </Root>
  );
};

SeasonsHistory.fragments = {
  footballRivalsSeason: gql`
    fragment SeasonsHistory_footballRivalsSeason on FootballRivalsSeason {
      slug
      id
      startDate
      endDate
      ranking(userSlug: $userSlug) {
        id
        points
        ranking
        divisionChange
        divisionConfig {
          id
          shortTitle
        }
      }
    }
  ` as TypedDocumentNode<SeasonsHistory_footballRivalsSeason>,
};
